
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ContentProvider } from '@lighty';
import { StorageService, TranslateService } from '@services';
import { CarouselComponent, OwlOptions } from 'ngx-owl-carousel-o';
import { LearnApi, AcademicProfileResume, CalendarEvents } from '@lighty';
import { environment } from '@env/environment';
import { CourseStats } from '@components/courses-stats-card/courses-stats-card.component';
import { GrikySkillsService } from '../../../../modules/griky-skills/services/griky-skills.service';
import { GrikyAchievementsResult, GapAnalysisResult } from '../../../../modules/griky-skills/griky-skills.types';


interface CalendarDay {
  day: number;
  month: number;
  year: number;
  currentMonth: boolean;
  isToday: boolean;
}

@Component({
  selector: 'app-academy-page',
  templateUrl: './academy-page.component.html',
  styleUrls: ['./academy-page.component.scss']
})

export class AcademyPageComponent implements OnInit {
  @ViewChild('carousel', { static: false }) carousel!: CarouselComponent;
  @ViewChild('bioText', { static: false }) bioText?: ElementRef<HTMLParagraphElement>;

  // Biografía del sidebar: clamp a 5 líneas con "Ver más / Ver menos"
  public bioExpanded: boolean = false;
  public bioOverflowing: boolean = false;

  /** En móvil el sidebar (avatar, perfil completado, menú) empieza colapsado detrás de un botón. */
  public showMobileProfileSidebar: boolean = false;

  public currentDate: Date = new Date();
  public expanded: boolean = false;
  public calendarDays: CalendarDay[] = [];
  public selectedDay: CalendarDay | null = null;
  public percent: number = 75;
  public selectedButton: string = 'Level';
  public progressLabel: string = 'Level';
  public company: any;
  public contents: any[] = [];
  public recentViews: any[] = [];
  public isLoading: boolean = false;
  public isLoadingRV: boolean = false;
  readonly circumference: number = 251.2;
  public me: any;
     /* btn resposnive de eventos */
    public isCalendarOpen: boolean = false;
  // Nuevas propiedades para progreso independiente de cada botón
  public buttonPercents: number[] = [100, 50, 0, 30];
  public buttonLabels: string[] = ['', '', '', ''];
  public currentPage: number = 1;
  public perPage: number = 4;
  public totalPages: number = 0;
  public pagination: any = null;
  public searchQuery: string = '';

  public currentPageRV: number = 1;
  public perPageRV: number = 10;
  public totalPagesRV: number = 0;
  public paginationRV: any = null;

  public carouselOptions: OwlOptions = {
    loop: true,
    margin: 14, // Espacio entre los elementos
    stagePadding: 14, // Espacio en los lados del carrusel
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 400,
    nav: false,
    navText: ['', ''],
    center: false,
    startPosition: 0,
    items: 4,
    responsive: {
      0: { items: 1 },
      425: { items: 1 },
      540: { items: 1 },
      600: { items: 2 },
      768: { items: 4 },
      1360: { items: 4 },
      1536: { items: 4 },
      1600: { items: 5 }
    }


  };

  //1. Skeleton loader flag
  public isLoadingSkeleton = true;
  skeletonItems = new Array(4);


  public academicProfile: AcademicProfileResume | null = null;
  public isLoadingProfile: boolean = false;

  // Certificados obtenidos: sección certificatesObtained del perfil académico.
  public certificates: any[] = [];

  public calendarEvents: CalendarEvents | null = null;
  public isLoadingEvents: boolean = false;

  public startDate: string = '';
  public endDate: string = '';

  public experienceLogo: string = '';

  // Course Stats para la nueva tarjeta de estadísticas
  public courseStats: CourseStats = {
    totalCourses: 44,
    coursesInProgress: 22,
    completedCourses: 0,
    totalEnrolled: 22
  };

  // Cursos para la tarjeta "Continúa aprendiendo"
  public continueLearningCourses: any[] = [];

  // Último curso/ruta visto (para el banner hero "Curso en progreso")
  public lastViewedCourse: any = null;

  // Habilidades adquiridas (módulo griky-skills) para "Tus habilidades adquiridas"
  public acquiredSkills: any[] = [];
  public grikySkillsEnabled = false;
  public skillsExpanded: boolean = false;

  // Logros (módulo griky-skills) para la fila de estadísticas y "Mis logros"
  public achievementsData: GrikyAchievementsResult | null = null;
  public isLoadingAchievements: boolean = false;

  // Objetivo del usuario (módulo griky-skills) para "Mis Competencias > Resumen de tu perfil"
  public objectiveRole: string = '—';
  public objectiveTime: string = '—';
  public routeSummary: string = '—';
  public gapAnalysis: GapAnalysisResult | null = null;

  // Filtros para tabla de estado del curso
  public statusFilter: 'all' | 'in-progress' | 'completed' | 'not-started' = 'all';
  public filteredContents: any[] = [];   // cursos visibles en la página actual
  // Todos los cursos (todas las páginas) para que el filtro aplique a la totalidad
  public allContents: any[] = [];
  public allContentsShuffled: any[] = []; // copia barajada estable para "Todos"
  public filteredAll: any[] = [];          // todos los cursos tras aplicar el filtro

  // Contadores reales por estado de progreso (para el sidebar)
  public statusCounts = { completed: 0, inProgress: 0, notStarted: 0, total: 0 };

  constructor(
    private storageService: StorageService,
    private contentProvider: ContentProvider,
    private learnApi: LearnApi,
    private grikySkillsService: GrikySkillsService,
    private translateService: TranslateService
  ) {
    this.company = this.storageService.get('company');
    this.me = this.storageService.get('me');
    this.grikySkillsEnabled = !!(this.company as any)?.permissions?.grikySkillsModule;
  }

  /** true solo si el usuario subió una foto real (no el avatar generado por defecto) */
  get hasUploadedPicture(): boolean {
    return !!(this.me && this.me.picture && this.me.picture.indexOf('ui-avatars.com') === -1);
  }

  /** Nombre corto del usuario activo: primer nombre + primer apellido. */
  get shortName(): string {
    const first = (this.me?.firstname || '').trim().split(/\s+/)[0] || '';
    const last = (this.me?.lastname || '').trim().split(/\s+/)[0] || '';
    return (first + ' ' + last).trim() || (this.me?.name || '');
  }

  // ===== Pestañas del sidebar (mismo patrón que academy-public-page) =====
  public activeTab: 'resumen' | 'certificados' | 'profesional' | 'habilidades' | 'competencias' | 'actividad' | 'logros' = 'resumen';

  public setTab(tab: 'resumen' | 'certificados' | 'profesional' | 'habilidades' | 'competencias' | 'actividad' | 'logros'): void {
    this.activeTab = tab;
  }

  /** true si estamos en la pestaña "Resumen" (todas las secciones). */
  public get isResumen(): boolean {
    return this.activeTab === 'resumen';
  }

  /** Una sección se muestra en "Resumen" o cuando su propia pestaña está activa. */
  public showSection(section: 'certificados' | 'profesional' | 'habilidades' | 'competencias' | 'actividad' | 'logros'): boolean {
    return this.isResumen || this.activeTab === section;
  }

  /** Nivel del usuario (módulo griky-skills), para la insignia del avatar. */
  public get userLevel(): number | null {
    return this.achievementsData?.level?.level ?? null;
  }

  // ===== Perfil completado (CON LOGICA: campos reales editables en /account/profile, ponderados por peso) =====
  private get profileCompletionFields(): Array<{ filled: boolean; labelKey: string; action: 'profile' | 'experience' | 'education' | 'certification'; weight: number }> {
    return [
      { filled: this.experiences.length > 0, labelKey: 'words.profile-missing-experience', action: 'experience', weight: 20 },
      { filled: this.education.length > 0, labelKey: 'words.profile-missing-education', action: 'education', weight: 20 },
      { filled: this.externalCerts.length > 0, labelKey: 'words.add-certifications', action: 'certification', weight: 20 },
      { filled: this.hasUploadedPicture, labelKey: 'words.profile-missing-picture', action: 'profile', weight: 15 },
      { filled: !!this.me?.biography, labelKey: 'words.profile-missing-biography', action: 'profile', weight: 15 },
      { filled: !!this.me?.headline, labelKey: 'words.profile-missing-headline', action: 'profile', weight: 10 }
    ];
  }

  public get profileCompletionPercent(): number {
    const fields = this.profileCompletionFields;
    const filledWeight = fields.filter(f => f.filled).reduce((sum, f) => sum + f.weight, 0);
    return Math.round(filledWeight);
  }

  /** Siguiente campo pendiente para completar el perfil (se usa en "Otras formas de fortalecerlo"). */
  public get nextMissingProfileField(): { labelKey: string; action: 'profile' | 'experience' | 'education' | 'certification' } | null {
    return this.profileCompletionFields.find(f => !f.filled) || null;
  }

  /** Acción del botón dinámico de "Otras formas de fortalecerlo" según el dato faltante. */
  public handleMissingFieldClick(): void {
    if (this.nextMissingProfileField?.action === 'certification') {
      this.openCertificationModal();
    } else if (this.nextMissingProfileField?.action === 'experience') {
      this.openExperienceModal();
    } else if (this.nextMissingProfileField?.action === 'education') {
      this.openEducationModal();
    }
  }

  // ===== Desplegable "Otras formas de fortalecerlo" =====
  public showOtherWays = false;

  public toggleOtherWays(): void {
    this.showOtherWays = !this.showOtherWays;
  }

  /** Abre el perfil público en una pestaña nueva (misma URL que usa "Compartir"). */
  public viewAsVisitor(): void {
    if (this.shareUrl) {
      window.open(this.shareUrl, '_blank');
      return;
    }
    const companyId = this.company?.id;
    if (companyId) {
      this.learnApi.shareProfile(companyId).subscribe({
        next: (res) => {
          this.shareUrl = `${window.location.origin}/profile/${res?.slug || ''}`;
          window.open(this.shareUrl, '_blank');
        },
        error: () => window.open(window.location.href, '_blank')
      });
    } else {
      window.open(window.location.href, '_blank');
    }
  }

  // ===== Compartir (popover redes + copiar enlace; mismo patrón que academy-public-page) =====
  public showShareMenu = false;
  public shareUrl = '';
  public linkCopied = false;

  public toggleShare(): void {
    this.showShareMenu = !this.showShareMenu;
    if (this.showShareMenu && !this.shareUrl) { this.buildShareUrl(); }
  }

  public closeShare(): void {
    this.showShareMenu = false;
  }

  /** URL pública del perfil (origin/profile/{slug}), independiente de GrikySkills. */
  private buildShareUrl(): void {
    const origin = window.location.origin;
    const companyId = this.company?.id;
    if (companyId) {
      this.learnApi.shareProfile(companyId).subscribe({
        next: (res) => { this.shareUrl = `${origin}/profile/${res?.slug || ''}`; },
        error: () => { this.shareUrl = window.location.href; }
      });
    } else {
      this.shareUrl = window.location.href;
    }
  }

  public get shareFacebook(): string { return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}`; }
  public get shareTwitter(): string { return `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.shareUrl)}&text=${encodeURIComponent('Mira este perfil')}`; }
  public get shareLinkedIn(): string { return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUrl)}`; }
  public get shareEmail(): string { return `mailto:?subject=${encodeURIComponent('Mira este perfil')}&body=${encodeURIComponent(this.shareUrl)}`; }

  public copyLink(): void {
    if (!this.shareUrl) { return; }
    const nav: any = window.navigator;
    if (nav.clipboard) {
      nav.clipboard.writeText(this.shareUrl).then(() => {
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      }).catch(() => { });
    }
  }

  // ===== Importar perfil (modal de selección de origen) =====
  public showImportModal = false;

  public openImportModal(): void {
    this.showImportModal = true;
  }

  public closeImportModal(): void {
    this.showImportModal = false;
  }

  // ===== Importar perfil desde CV (PDF/.docx) =====
  @ViewChild('cvFileInput') cvFileInput?: ElementRef<HTMLInputElement>;

  public cvImportStatus: 'idle' | 'uploading' | 'processing' | 'preview' | 'error' = 'idle';
  public cvImportErrorMessage: string = '';
  public cvPreview: { experiences: any[]; education: any[]; externalCertifications: any[] } | null = null;
  private cvImportPollAttempts = 0;
  private readonly cvImportMaxPollAttempts = 20; // ~40s a 2s por intento

  /** Abre el selector de archivo nativo (input oculto) desde el botón "Desde un CV". */
  public triggerCvUpload(): void {
    this.cvImportStatus = 'idle';
    this.cvImportErrorMessage = '';
    this.cvFileInput?.nativeElement.click();
  }

  public onCvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // permite volver a elegir el mismo archivo si el usuario reintenta

    if (!file) { return; }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'pdf' && ext !== 'docx') {
      this.cvImportStatus = 'error';
      this.cvImportErrorMessage = 'Solo se aceptan archivos PDF o Word (.docx).';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.cvImportStatus = 'error';
      this.cvImportErrorMessage = 'El archivo es muy grande (máximo 5MB).';
      return;
    }

    this.cvImportStatus = 'uploading';
    this.learnApi.createCvImport(this.company.id, file).subscribe({
      next: (res) => {
        this.cvImportStatus = 'processing';
        this.cvImportPollAttempts = 0;
        this.pollCvImportStatus(res.id);
      },
      error: (err) => {
        this.cvImportStatus = 'error';
        this.cvImportErrorMessage = this.cvImportErrorFromResponse(err);
      }
    });
  }

  private pollCvImportStatus(cvImportId: number): void {
    this.learnApi.getCvImportStatus(this.company.id, cvImportId).subscribe({
      next: (res) => {
        if (res.status === 'processing') {
          this.cvImportPollAttempts++;
          if (this.cvImportPollAttempts >= this.cvImportMaxPollAttempts) {
            this.cvImportStatus = 'error';
            this.cvImportErrorMessage = 'Esto está tardando más de lo esperado. Intenta de nuevo más tarde.';
            return;
          }
          setTimeout(() => this.pollCvImportStatus(cvImportId), 2000);
          return;
        }

        if (res.status === 'failed') {
          this.cvImportStatus = 'error';
          this.cvImportErrorMessage = res.errorMessage || 'No pudimos procesar este archivo.';
          return;
        }

        this.cvPreview = {
          experiences: (res.data?.experiences || []).map((e: any) => ({ ...e, selected: true })),
          education: (res.data?.education || []).map((e: any) => ({ ...e, selected: true })),
          externalCertifications: (res.data?.externalCertifications || []).map((c: any) => ({ ...c, selected: true }))
        };
        this.cvImportStatus = 'preview';
      },
      error: () => {
        this.cvImportStatus = 'error';
        this.cvImportErrorMessage = 'No pudimos consultar el estado de la importación.';
      }
    });
  }

  private cvImportErrorFromResponse(err: any): string {
    if (err?.status === 413 || err?.status === 422) { return 'El archivo es muy grande (máximo 5MB) o no es un PDF/Word válido.'; }
    return 'No pudimos subir el archivo. Intenta de nuevo.';
  }

  public cvEducationLabel(edu: any): string {
    return [edu.degree, edu.studyArea].filter((v) => !!v).join(' ');
  }

  public toggleCvPreviewItem(list: 'experiences' | 'education' | 'externalCertifications', index: number): void {
    if (!this.cvPreview) { return; }
    this.cvPreview[list][index].selected = !this.cvPreview[list][index].selected;
  }

  public cancelCvImportPreview(): void {
    this.cvPreview = null;
    this.cvImportStatus = 'idle';
    this.closeImportModal();
  }

  /** Guarda los ítems marcados de la previsualización usando los endpoints reales ya existentes. */
  public confirmCvImportPreview(): void {
    if (!this.cvPreview) { return; }
    const companyId = this.company.id;

    const experienceRequests = this.cvPreview.experiences
      .filter((e) => e.selected)
      .map((e) => this.learnApi.createExperience(companyId, {
        title: e.title,
        companyName: e.companyName,
        startMonth: e.startMonth,
        startYear: e.startYear,
        endMonth: e.isCurrent ? null : e.endMonth,
        endYear: e.isCurrent ? null : e.endYear,
        isCurrent: !!e.isCurrent,
        description: e.description || null
      }));

    const educationRequests = this.cvPreview.education
      .filter((e) => e.selected)
      .map((e) => this.learnApi.createEducation(companyId, {
        university: e.university,
        degree: e.degree || null,
        studyArea: e.studyArea || null,
        startMonth: e.startMonth,
        startYear: e.startYear,
        endMonth: e.endMonth,
        endYear: e.endYear
      }));

    const certificationRequests = this.cvPreview.externalCertifications
      .filter((c) => c.selected)
      .map((c) => this.learnApi.createExternalCertification(companyId, {
        name: c.name,
        issuer: c.issuer,
        issueMonth: c.issueMonth,
        issueYear: c.issueYear
      }));

    const allRequests = [...experienceRequests, ...educationRequests, ...certificationRequests];

    if (allRequests.length === 0) {
      this.cancelCvImportPreview();
      return;
    }

    forkJoin(allRequests).subscribe({
      next: () => {
        this.loadExperiences();
        this.loadEducation();
        this.loadExternalCertifications();
        this.cancelCvImportPreview();
      },
      error: () => {
        this.cvImportStatus = 'error';
        this.cvImportErrorMessage = 'Algunos elementos no se pudieron guardar. Intenta de nuevo.';
      }
    });
  }

  // ===========================================================================
  // Perfil profesional: Experiencia / Educación / Certificaciones (mismo patrón
  // que academy-public-page). UI + datos mock locales. Al guardar se agrega al
  // arreglo correspondiente y se ve de inmediato en el perfil. Autocompletes con
  // catálogos reales y persistencia en backend quedan pendientes.
  // ===========================================================================

  public collapsed = { experience: false, education: false, certifications: false };

  /** Pestañas internas de la tarjeta "Mi perfil profesional": Resumen (vista compacta) o una sección completa. */
  public profileSubTab: 'resumen' | 'experience' | 'education' | 'certifications' | 'skills' = 'resumen';

  public setProfileSubTab(tab: 'resumen' | 'experience' | 'education' | 'certifications' | 'skills'): void {
    this.profileSubTab = tab;
  }

  public experiences: any[] = [];
  public education: any[] = [];
  public externalCerts: any[] = [];

  /** Experiencia ordenada por año (más reciente primero; "Actual" siempre de primero). */
  public get sortedExperiences(): any[] {
    return [...this.experiences].sort((a, b) => {
      if (a.current && !b.current) { return -1; }
      if (!a.current && b.current) { return 1; }
      return (b.startYear || 0) - (a.startYear || 0);
    });
  }

  /** Educación ordenada por año (más reciente primero). */
  public get sortedEducation(): any[] {
    return [...this.education].sort((a, b) => (b.startYear || 0) - (a.startYear || 0));
  }

  /** Certificaciones ordenadas por año (más reciente primero). */
  public get sortedExternalCerts(): any[] {
    return [...this.externalCerts].sort((a, b) => (b.issueYear || 0) - (a.issueYear || 0));
  }

  /** Cantidad visible de cada lista en las pestañas completas de "Mi perfil profesional" (ver más de 5 en 5). */
  public experienceVisibleCount = 5;
  public educationVisibleCount = 5;
  public externalCertsVisibleCount = 5;

  public showMoreExperience(): void { this.experienceVisibleCount += 5; }
  public showMoreEducation(): void { this.educationVisibleCount += 5; }
  public showMoreExternalCerts(): void { this.externalCertsVisibleCount += 5; }

  // Id del ítem en edición (null = el modal está en modo "Agregar").
  public editingExperienceId: number | null = null;
  public editingEducationId: number | null = null;
  public editingCertificationId: number | null = null;

  public showExperienceModal = false;
  public showEducationModal = false;
  public showCertificationModal = false;

  public companySelected = false;
  public universitySelected = false;
  public issuerSelected = false;

  public companyOptions: string[] = [
    'Griky', 'Griyid', 'Innovate Corp.', 'TechStart', 'CodeLab',
    'Google', 'Microsoft', 'Amazon', 'Platzi', 'Netzun'
  ];

  public countryOptions: string[] = [
    'Colombia', 'México', 'Argentina', 'Perú', 'Chile', 'España', 'Estados Unidos'
  ];

  public universitiesByCountry: { [country: string]: Array<{ name: string; city: string }> } = {
    'Colombia': [
      { name: 'Universidad Nacional de Colombia', city: 'Bogotá' },
      { name: 'Universidad de los Andes', city: 'Bogotá' },
      { name: 'Pontificia Universidad Javeriana', city: 'Bogotá' },
      { name: 'Universidad de Antioquia', city: 'Medellín' },
      { name: 'Universidad del Valle', city: 'Cali' }
    ],
    'México': [
      { name: 'Universidad Nacional Autónoma de México', city: 'Ciudad de México' },
      { name: 'Instituto Politécnico Nacional', city: 'Ciudad de México' },
      { name: 'Tecnológico de Monterrey', city: 'Monterrey' }
    ],
    'Argentina': [
      { name: 'Universidad de Buenos Aires', city: 'Buenos Aires' },
      { name: 'Universidad Nacional de Córdoba', city: 'Córdoba' }
    ],
    'Perú': [
      { name: 'Universidad Nacional Mayor de San Marcos', city: 'Lima' },
      { name: 'Pontificia Universidad Católica del Perú', city: 'Lima' }
    ],
    'Chile': [
      { name: 'Universidad de Chile', city: 'Santiago' },
      { name: 'Pontificia Universidad Católica de Chile', city: 'Santiago' }
    ],
    'España': [
      { name: 'Universidad Complutense de Madrid', city: 'Madrid' },
      { name: 'Universidad de Barcelona', city: 'Barcelona' }
    ],
    'Estados Unidos': [
      { name: 'Massachusetts Institute of Technology', city: 'Cambridge' },
      { name: 'Stanford University', city: 'Stanford' }
    ]
  };

  public degreeOptions: string[] = [
    'Técnico', 'Tecnólogo', 'Pregrado/BSc', 'Especialización', 'Maestría/MSc', 'Doctorado/PhD'
  ];

  public studyAreaSuggestions: string[] = [
    'Ingeniería de Software', 'Ingeniería de Sistemas', 'Administración de Empresas',
    'Diseño Gráfico', 'Marketing Digital', 'Ciencia de Datos', 'Contaduría'
  ];

  public issuerOptions: Array<{ name: string; logo?: string }> = [
    { name: 'Meta', logo: 'https://cdn.simpleicons.org/meta/0081FB' },
    { name: 'Google', logo: 'https://cdn.simpleicons.org/google/4285F4' },
    { name: 'AWS', logo: 'https://cdn.simpleicons.org/amazonwebservices/232F3E' },
    { name: 'Coursera', logo: 'https://cdn.simpleicons.org/coursera/0056D2' },
    { name: 'Microsoft', logo: 'https://cdn.simpleicons.org/microsoft/5E5E5E' },
    { name: 'Platzi', logo: 'https://cdn.simpleicons.org/platzi/98CA3F' }
  ];

  public months = [
    { v: 1, l: 'Ene' }, { v: 2, l: 'Feb' }, { v: 3, l: 'Mar' }, { v: 4, l: 'Abr' },
    { v: 5, l: 'May' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Ago' },
    { v: 9, l: 'Sep' }, { v: 10, l: 'Oct' }, { v: 11, l: 'Nov' }, { v: 12, l: 'Dic' }
  ];
  public years: number[] = [];

  public expForm: any = this.emptyExpForm();
  public eduForm: any = this.emptyEduForm();
  public certForm: any = this.emptyCertForm();

  private emptyExpForm(): any {
    return {
      title: '', company: '', companyManual: false,
      startMonth: null, startYear: null, endMonth: null, endYear: null,
      current: false, description: ''
    };
  }

  private emptyEduForm(): any {
    return {
      country: this.inferCountry(), university: '', universityManual: false,
      degree: '', area: '', startMonth: null, startYear: null, endMonth: null, endYear: null
    };
  }

  private emptyCertForm(): any {
    return {
      name: '', issuer: '', issuerLogo: '', issuerManual: false,
      issueMonth: null, issueYear: null, expMonth: null, expYear: null, url: ''
    };
  }

  /** Pre-selecciona el país a partir de la ciudad configurada en el perfil. */
  private inferCountry(): string {
    const city = (this.me?.city || '').toString().trim().toLowerCase();
    const map: { [k: string]: string } = {
      'bogota': 'Colombia', 'bogotá': 'Colombia', 'medellin': 'Colombia', 'medellín': 'Colombia',
      'cali': 'Colombia', 'ciudad de mexico': 'México', 'cdmx': 'México', 'monterrey': 'México',
      'buenos aires': 'Argentina', 'lima': 'Perú', 'santiago': 'Chile', 'madrid': 'España',
      'barcelona': 'España'
    };
    return map[city] || 'Colombia';
  }

  private buildYears(): void {
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let y = currentYear; y >= 1975; y--) {
      this.years.push(y);
    }
  }

  /** Colapsa/expande un bloque del perfil profesional. */
  public toggleCollapse(section: 'experience' | 'education' | 'certifications'): void {
    this.collapsed[section] = !this.collapsed[section];
  }

  public openExperienceModal(item?: any): void {
    if (item) {
      this.editingExperienceId = item.id;
      this.expForm = {
        title: item.rawTitle || '', company: item.rawCompanyName || '', companyManual: true,
        startMonth: item.startMonth || null, startYear: item.startYear || null,
        endMonth: item.endMonth || null, endYear: item.endYear || null,
        current: !!item.isCurrent, description: item.description || ''
      };
      this.companySelected = true;
    } else {
      this.editingExperienceId = null;
      this.expForm = this.emptyExpForm();
      this.companySelected = false;
    }
    this.showExperienceModal = true;
  }

  public openEducationModal(item?: any): void {
    if (item) {
      this.editingEducationId = item.id;
      this.eduForm = {
        country: this.inferCountry(), university: item.university || '', universityManual: true,
        degree: item.rawDegree || '', area: item.rawStudyArea || '',
        startMonth: item.startMonth || null, startYear: item.startYear || null,
        endMonth: item.endMonth || null, endYear: item.endYear || null
      };
      this.universitySelected = true;
    } else {
      this.editingEducationId = null;
      this.eduForm = this.emptyEduForm();
      this.universitySelected = false;
    }
    this.showEducationModal = true;
  }

  public openCertificationModal(item?: any): void {
    if (item) {
      this.editingCertificationId = item.id;
      this.certForm = {
        name: item.rawName || '', issuer: item.rawIssuer || '', issuerLogo: item.logo || '', issuerManual: true,
        issueMonth: item.issueMonth || null, issueYear: item.issueYear || null,
        expMonth: item.expirationMonth || null, expYear: item.expirationYear || null,
        url: item.verificationUrl || ''
      };
      this.issuerSelected = true;
    } else {
      this.editingCertificationId = null;
      this.certForm = this.emptyCertForm();
      this.issuerSelected = false;
    }
    this.showCertificationModal = true;
  }

  public closeModals(): void {
    this.showExperienceModal = false;
    this.showEducationModal = false;
    this.showCertificationModal = false;
  }

  // --- Autocomplete Empresa ---
  public get filteredCompanies(): string[] {
    const q = (this.expForm.company || '').toLowerCase().trim();
    const list = q ? this.companyOptions.filter(c => c.toLowerCase().includes(q)) : this.companyOptions;
    return list.slice(0, 6);
  }
  public get showCompanySuggestions(): boolean {
    return !!this.expForm.company && !this.expForm.companyManual && !this.companySelected;
  }
  public onCompanyInput(): void { this.companySelected = false; }
  public selectCompany(name: string): void { this.expForm.company = name; this.companySelected = true; }
  public enableManualCompany(): void { this.expForm.companyManual = true; this.companySelected = true; }

  // --- Autocomplete Universidad (dependiente del país) ---
  public get filteredUniversities(): Array<{ name: string; city: string }> {
    const list = this.universitiesByCountry[this.eduForm.country] || [];
    const q = (this.eduForm.university || '').toLowerCase().trim();
    const filtered = q ? list.filter(u => u.name.toLowerCase().includes(q)) : list;
    return filtered.slice(0, 6);
  }
  public get showUniversitySuggestions(): boolean {
    return !!this.eduForm.country && !!this.eduForm.university
      && !this.eduForm.universityManual && !this.universitySelected;
  }
  public onUniversityInput(): void { this.universitySelected = false; }
  public onCountryChange(): void { this.eduForm.university = ''; this.universitySelected = false; }
  public selectUniversity(u: { name: string; city: string }): void {
    this.eduForm.university = u.name;
    this.universitySelected = true;
  }
  public enableManualUniversity(): void { this.eduForm.universityManual = true; this.universitySelected = true; }

  // --- Autocomplete Organismo emisor ---
  public get filteredIssuers(): Array<{ name: string; logo?: string }> {
    const q = (this.certForm.issuer || '').toLowerCase().trim();
    const list = q ? this.issuerOptions.filter(i => i.name.toLowerCase().includes(q)) : this.issuerOptions;
    return list.slice(0, 6);
  }
  public get showIssuerSuggestions(): boolean {
    return !!this.certForm.issuer && !this.certForm.issuerManual && !this.issuerSelected;
  }
  public onIssuerInput(): void { this.issuerSelected = false; this.certForm.issuerLogo = ''; }
  public selectIssuer(i: { name: string; logo?: string }): void {
    this.certForm.issuer = i.name;
    this.certForm.issuerLogo = i.logo || '';
    this.issuerSelected = true;
  }
  public enableManualIssuer(): void { this.certForm.issuerManual = true; this.issuerSelected = true; }

  // --- Formato de fechas mes/año ---
  private monthLabel(m: any): string {
    const found = this.months.find(x => x.v === +m);
    return found ? found.l : '';
  }
  private formatMonthYear(m: any, y: any): string {
    if (!y) { return ''; }
    const ml = this.monthLabel(m);
    return ml ? `${ml} ${y}` : `${y}`;
  }
  private formatPeriod(sM: any, sY: any, eM: any, eY: any, current: boolean): string {
    const start = this.formatMonthYear(sM, sY);
    const end = current ? 'Actualidad' : this.formatMonthYear(eM, eY);
    return [start, end].filter(Boolean).join(' - ');
  }

  // --- Mapeo de la respuesta estructurada del API al shape de display que ya usa el HTML ---
  private mapExperience(e: any): any {
    return {
      id: e.id,
      rawTitle: e.title,
      rawCompanyName: e.companyName,
      startMonth: e.startMonth,
      startYear: e.startYear,
      endMonth: e.endMonth,
      endYear: e.endYear,
      isCurrent: e.isCurrent,
      description: e.description,
      title: e.companyName ? `${e.title} en ${e.companyName}` : e.title,
      period: this.formatPeriod(e.startMonth, e.startYear, e.endMonth, e.endYear, e.isCurrent),
      current: e.isCurrent
    };
  }

  private mapEducation(e: any): any {
    return {
      id: e.id,
      university: e.university,
      rawDegree: e.degree,
      rawStudyArea: e.studyArea,
      startMonth: e.startMonth,
      startYear: e.startYear,
      endMonth: e.endMonth,
      endYear: e.endYear,
      career: [e.degree, e.studyArea].filter(Boolean).join(' '),
      period: this.formatPeriod(e.startMonth, e.startYear, e.endMonth, e.endYear, false)
    };
  }

  private mapCertification(c: any): any {
    return {
      id: c.id,
      rawName: c.name,
      rawIssuer: c.issuer,
      issueMonth: c.issueMonth,
      issueYear: c.issueYear,
      expirationMonth: c.expirationMonth,
      expirationYear: c.expirationYear,
      verificationUrl: c.verificationUrl,
      title: c.name,
      org: c.issuer,
      logo: c.issuerLogo || undefined,
      date: this.formatMonthYear(c.issueMonth, c.issueYear)
    };
  }

  /** Trae la experiencia laboral real del usuario para esta company. */
  public loadExperiences(): void {
    this.learnApi.getExperiences(this.company.id).subscribe({
      next: (data) => { this.experiences = (data || []).map((e) => this.mapExperience(e)); },
      error: (err) => { console.error('Error al cargar la experiencia laboral', err); this.experiences = []; }
    });
  }

  /** Trae la educación real del usuario para esta company. */
  public loadEducation(): void {
    this.learnApi.getEducation(this.company.id).subscribe({
      next: (data) => { this.education = (data || []).map((e) => this.mapEducation(e)); },
      error: (err) => { console.error('Error al cargar la educación', err); this.education = []; }
    });
  }

  /** Trae las certificaciones externas reales del usuario para esta company. */
  public loadExternalCertifications(): void {
    this.learnApi.getExternalCertifications(this.company.id).subscribe({
      next: (data) => { this.externalCerts = (data || []).map((c) => this.mapCertification(c)); },
      error: (err) => { console.error('Error al cargar las certificaciones externas', err); this.externalCerts = []; }
    });
  }

  // --- Guardar (crea o actualiza según haya o no un ítem en edición) ---
  public saveExperience(): void {
    const payload = {
      title: this.expForm.title,
      companyName: this.expForm.company,
      startMonth: this.expForm.startMonth,
      startYear: this.expForm.startYear,
      endMonth: this.expForm.current ? null : this.expForm.endMonth,
      endYear: this.expForm.current ? null : this.expForm.endYear,
      isCurrent: this.expForm.current,
      description: this.expForm.description || null
    };

    const request$ = this.editingExperienceId
      ? this.learnApi.updateExperience(this.company.id, this.editingExperienceId, payload)
      : this.learnApi.createExperience(this.company.id, payload);

    request$.subscribe({
      next: (saved) => {
        const mapped = this.mapExperience(saved);
        if (this.editingExperienceId) {
          const idx = this.experiences.findIndex((e) => e.id === this.editingExperienceId);
          if (idx !== -1) { this.experiences[idx] = mapped; }
        } else {
          this.experiences.unshift(mapped);
        }
        this.closeModals();
      },
      error: (err) => console.error('Error al guardar la experiencia', err)
    });
  }

  public saveEducation(): void {
    const payload = {
      university: this.eduForm.university,
      degree: this.eduForm.degree || null,
      studyArea: this.eduForm.area || null,
      startMonth: this.eduForm.startMonth,
      startYear: this.eduForm.startYear,
      endMonth: this.eduForm.endMonth,
      endYear: this.eduForm.endYear
    };

    const request$ = this.editingEducationId
      ? this.learnApi.updateEducation(this.company.id, this.editingEducationId, payload)
      : this.learnApi.createEducation(this.company.id, payload);

    request$.subscribe({
      next: (saved) => {
        const mapped = this.mapEducation(saved);
        if (this.editingEducationId) {
          const idx = this.education.findIndex((e) => e.id === this.editingEducationId);
          if (idx !== -1) { this.education[idx] = mapped; }
        } else {
          this.education.unshift(mapped);
        }
        this.closeModals();
      },
      error: (err) => console.error('Error al guardar la educación', err)
    });
  }

  public saveCertification(): void {
    const payload = {
      name: this.certForm.name,
      issuer: this.certForm.issuer,
      issuerLogo: this.certForm.issuerLogo || null,
      issueMonth: this.certForm.issueMonth,
      issueYear: this.certForm.issueYear,
      expirationMonth: this.certForm.expMonth,
      expirationYear: this.certForm.expYear,
      verificationUrl: this.certForm.url || null
    };

    const request$ = this.editingCertificationId
      ? this.learnApi.updateExternalCertification(this.company.id, this.editingCertificationId, payload)
      : this.learnApi.createExternalCertification(this.company.id, payload);

    request$.subscribe({
      next: (saved) => {
        const mapped = this.mapCertification(saved);
        if (this.editingCertificationId) {
          const idx = this.externalCerts.findIndex((c) => c.id === this.editingCertificationId);
          if (idx !== -1) { this.externalCerts[idx] = mapped; }
        } else {
          this.externalCerts.unshift(mapped);
        }
        this.closeModals();
      },
      error: (err) => console.error('Error al guardar la certificación', err)
    });
  }

  // --- Eliminar ---
  public deleteExperience(item: any): void {
    if (!item?.id) { return; }
    this.learnApi.deleteExperience(this.company.id, item.id).subscribe({
      next: () => { this.experiences = this.experiences.filter((e) => e.id !== item.id); },
      error: (err) => console.error('Error al eliminar la experiencia', err)
    });
  }

  public deleteEducation(item: any): void {
    if (!item?.id) { return; }
    this.learnApi.deleteEducation(this.company.id, item.id).subscribe({
      next: () => { this.education = this.education.filter((e) => e.id !== item.id); },
      error: (err) => console.error('Error al eliminar la educación', err)
    });
  }

  public deleteCertification(item: any): void {
    if (!item?.id) { return; }
    this.learnApi.deleteExternalCertification(this.company.id, item.id).subscribe({
      next: () => { this.externalCerts = this.externalCerts.filter((c) => c.id !== item.id); },
      error: (err) => console.error('Error al eliminar la certificación', err)
    });
  }



   toggleCalendarPanel(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  ngOnInit(): void {
    this.updateCalendar();
    this.getAcademicProfile();
    // Se puede inicializar el progreso o actualizarlo a partir de datos recibidos
    this.updateProgress(this.percent, this.progressLabel);
    this.getContent();
    this.getCalendarEventsApi(null, null);
    this.recentContentViews();
    this.getAcquiredSkills();
    this.getObjectiveData();
    this.getRouteData();
    this.getAchievements();
    this.loadExperiences();
    this.loadEducation();
    this.loadExternalCertifications();
    if (this.grikySkillsEnabled) {
      this.getGapAnalysisData();
    }
    this.buildYears();
    if (!environment.production) {
      this.experienceLogo = this.company.experience.displayPicture.replace(
        "https://api-upgraded.myskillcamp.com.deltabluecdn.com",
        "https://api.myskillcamp.com"
      );
    }
  }

  ngAfterViewInit(): void {
    const section = document.getElementById("learn-section");
    if (section) {
      section.style.padding = "0";
    }
    this.checkBioOverflow();
  }

  /**
   * Determina si la biografía supera las 5 líneas (clamp) para mostrar el
   * enlace "Ver más". Compara la altura real del texto contra su altura visible
   * con el clamp aplicado.
   */
  private checkBioOverflow(): void {
    setTimeout(() => {
      const el = this.bioText?.nativeElement;
      if (!el) {
        this.bioOverflowing = false;
        return;
      }
      this.bioOverflowing = el.scrollHeight > el.clientHeight + 1;
    });
  }

  // Devuelve el mes y año formateados en español
  get monthYear(): string {
    return this.currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(' de ', ' ');
  }

  // Actualiza el arreglo de días a mostrar en el calendario
  updateCalendar(): void {
    this.calendarDays = [];
    const firstDayOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1).getDay();
    const daysInMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0).getDate();
    const today = new Date();
    const prevMonthDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 0);

    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const dayNum = prevMonthDate.getDate() - i;
      const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, dayNum);
      this.calendarDays.push({
        day: dayNum,
        month: date.getMonth(),
        year: date.getFullYear(),
        currentMonth: false,
        isToday: this.isSameDate(date, today)
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), i);
      this.calendarDays.push({
        day: i,
        month: this.currentDate.getMonth(),
        year: this.currentDate.getFullYear(),
        currentMonth: true,
        isToday: this.isSameDate(date, today)
      });
    }
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  get displayDays(): CalendarDay[] {
    return this.expanded ? this.calendarDays : this.calendarDays.slice(0, 7);
  }

  isSelected(day: CalendarDay): boolean {
    return this.selectedDay !== null &&
      day.day === this.selectedDay.day &&
      day.month === this.selectedDay.month &&
      day.year === this.selectedDay.year;
  }

  selectDay(day: CalendarDay): void {
    if (day.currentMonth) {
      this.selectedDay = day;
    } else {
      this.changeMonthAndSelectDay(day);
    }
    this.getCalendarEventsApi(day.day, day.month + 1);
  }

  changeMonthAndSelectDay(day: CalendarDay): void {
    this.currentDate = new Date(day.year, day.month, 1);
    this.updateCalendar();
    this.selectedDay = this.calendarDays.find(d => d.day === day.day && d.currentMonth) || null;
  }

  prevMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.updateCalendar();
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.updateCalendar();
  }

  toggleCalendarView(): void {
    this.expanded = !this.expanded;
  }

  /**
   * Actualiza el progreso general (no toca el progress existente)
   * @param percent Porcentaje del progreso
   * @param text Texto a mostrar
   */
  updateProgress(percent: number, text: string): void {
    this.percent = percent;
    this.progressLabel = text;
    this.selectedButton = text;
  }

  /**
   * Calcula el valor de strokeDashoffset según el progreso general
   */
  get dashOffset(): number {
    return this.circumference - (this.percent / 100) * this.circumference;
  }

  /**
   * Actualiza el progreso de un botón específico
   * @param index Índice del botón (0 a 3)
   * @param percent Porcentaje del progreso
   * @param text Etiqueta o descripción opcional
   */
  updateButtonProgress(index: number, percent: number, text: string): void {
    if (index >= 0 && index < this.buttonPercents.length) {
      this.buttonPercents[index] = percent;
      this.buttonLabels[index] = text;
    }
  }

  /**
   * Obtiene el dashOffset para el progreso circular de un botón dado
   * @param index Índice del botón (0 a 3)
   */
  getButtonDashOffset(index: number): number {
    return this.circumference - (this.buttonPercents[index] / 100) * this.circumference;
  }

  public prev(): void {
    this.carousel.prev();
  }

  public next(): void {
    this.carousel.next();
  }

  public recentContentViews(page: number = 1): void {
    this.recentViews = [];
    this.isLoadingRV = true;
    this.currentPageRV = page;

    const params: any = {
      company_id: this.company.id,
      per_page: this.perPageRV,
      page: this.currentPageRV
    };

    this.contentProvider.getContents(params).subscribe((data) => {
      this.recentViews = data.contents;
      this.paginationRV = data.pagination;
      this.totalPagesRV = this.pagination?.lastPage || 1;
      this.isLoadingRV = false;

      // Banner hero: el último curso/ruta visto más reciente que NO esté finalizado
      // (un curso al 100% no se muestra en el banner). Sin Skill habilitado, las rutas
      // de aprendizaje no se muestran en el banner (son parte de Skill).
      const last = (this.recentViews || [])
        .filter((c: any) => this.grikySkillsEnabled || c.type !== 'traject')
        .find((c: any) => (c?.data?.progress ?? 0) < 100) || null;
      const type = last?.type;
      const isTraject = type === 'traject';
      const isExternal = String(type || '').indexOf('external') !== -1;
      this.lastViewedCourse = last
        ? {
            id: last.data?.id ?? last.id,
            type,
            title: last.data?.title || last.title || 'Sin título',
            image: this.getContentPicture(last),
            progress: last.data?.progress ?? 0,
            urlDetail: last.data?.urlDetail || last.urlDetail || '',
            // Datos del banner: proveedor + tipo de contenido (complementa al tipo bajo el título).
            providerName: this.getProviderName(last),
            typeLabelKey: isTraject
              ? 'words.trajectory'
              : (isExternal ? 'api.content-type.external_content' : 'words.internal-content'),
            isExternal
          }
        : null;

      // Con los últimos vistos cargados, actualizar "Continúa donde lo dejaste" (índices 1..3)
      this.updateContinueLearningCourses();
    });
  }


  /**
   * Búsqueda en tiempo real (client-side): filtra sobre los cursos ya cargados,
   * sin llamadas al servidor. Al borrar, se restablece al instante.
   */
  public onSearchChange(_value?: string): void {
    this.currentPage = 1;
    this.applyStatusFilter();
  }

  /**
   * Normaliza texto para comparar ignorando mayúsculas y acentos.
   */
  private normalizeText(text: string): string {
    return (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  /**
   * Carga las habilidades adquiridas del usuario (módulo griky-skills)
   * para la tarjeta "Tus habilidades adquiridas".
   */
  public getAcquiredSkills(): void {
    this.grikySkillsService.getUserSkills(this.company.id).subscribe({
      next: (skills) => {
        console.log('[Academy] getUserSkills response:', skills);
        this.acquiredSkills = skills || [];
      },
      error: (err) => {
        console.error('Error al cargar habilidades adquiridas', err);
        this.acquiredSkills = [];
      }
    });
  }

  /**
   * Carga el objetivo del usuario (módulo griky-skills): rol objetivo y tiempo estimado.
   */
  public getObjectiveData(): void {
    this.grikySkillsService.getObjective(this.company.id).subscribe({
      next: (obj) => {
        console.log('[Academy] getObjective response:', obj);
        this.objectiveRole = obj?.targetRole || '—';
        this.objectiveTime = this.formatTimeline(obj?.timeline);
      },
      error: (err) => {
        console.error('Error al cargar el objetivo', err);
      }
    });
  }

  /**
   * Carga las rutas de aprendizaje del usuario y genera el resumen para el widget.
   */
  public getRouteData(): void {
    this.grikySkillsService.getUserLearningPaths(this.company.id).subscribe({
      next: (paths) => {
        const active = (paths || []).filter((p: any) => p.status === 'active');
        if (active.length === 0) {
          this.routeSummary = '—';
        } else if (active.length === 1) {
          this.routeSummary = active[0].name || active[0].targetRole || '—';
        } else {
          this.routeSummary = `${active.length} rutas activas`;
        }
      },
      error: () => { this.routeSummary = '—'; }
    });
  }

  /**
   * Carga el análisis de brechas (módulo griky-skills) para calcular el % de match
   * con el rol objetivo en "Mis Competencias".
   */
  public getGapAnalysisData(): void {
    this.grikySkillsService.getGapAnalysis(this.company.id).subscribe({
      next: (data) => { this.gapAnalysis = data; },
      error: () => { this.gapAnalysis = null; }
    });
  }

  /** % de match entre el nivel actual de habilidades y los requisitos del rol objetivo. */
  public get matchPercent(): number | null {
    const requirements = this.gapAnalysis?.requirements;
    if (this.gapAnalysis?.status !== 'ok' || !requirements || requirements.length === 0) {
      return null;
    }
    const avgRatio = requirements.reduce((sum, r) => {
      const ratio = r.recommendedLevel > 0 ? Math.min(r.currentLevel / r.recommendedLevel, 1) : 1;
      return sum + ratio;
    }, 0) / requirements.length;
    return Math.round(avgRatio * 100);
  }

  /** Cantidad de competencias del rol objetivo que aún no alcanzan el nivel recomendado. */
  public get missingCompetencies(): number | null {
    const requirements = this.gapAnalysis?.requirements;
    if (this.gapAnalysis?.status !== 'ok' || !requirements || requirements.length === 0) {
      return null;
    }
    return requirements.filter(r => r.currentLevel < r.recommendedLevel).length;
  }

  /** Contenidos (cursos/rutas) actualmente en progreso (0 < progress < 100). */
  private get activeContents(): any[] {
    return (this.allContents || []).filter((c: any) => {
      const p = c?.data?.progress ?? 0;
      return p > 0 && p < 100;
    });
  }

  public get activeContentsCount(): number {
    return this.activeContents.length;
  }

  public get hasActiveContent(): boolean {
    return this.activeContentsCount > 0;
  }

  public get activeContentBreakdownText(): string {
    const courses = this.activeContents.filter((c: any) => (c?.type || c?.data?.type) !== 'route').length;
    const routes = this.activeContents.length - courses;
    const parts: string[] = [];
    if (courses > 0) { parts.push(`${courses} curso${courses === 1 ? '' : 's'}`); }
    if (routes > 0) { parts.push(`${routes} ruta${routes === 1 ? '' : 's'}`); }
    return parts.join(' · ');
  }

  public get weightedProgressPercent(): number {
    const contents = this.allContents || [];
    if (!contents.length) { return 0; }
    const total = contents.reduce((sum: number, c: any) => sum + (c?.data?.progress ?? 0), 0);
    return Math.round(total / contents.length);
  }

  /** Nombre del contenido en curso con más avance (candidato al próximo certificado). Null si no hay ninguno en curso. */
  public get nearestActiveContentName(): string | null {
    const contents = this.activeContents;
    if (!contents.length) { return null; }
    const best = contents.reduce((a: any, b: any) => (b?.data?.progress ?? 0) > (a?.data?.progress ?? 0) ? b : a);
    return best?.data?.title || best?.title || null;
  }

  /**
   * Carga los logros del usuario (módulo griky-skills) para la fila de estadísticas
   * y la sección "Mis logros".
   */
  public getAchievements(): void {
    this.isLoadingAchievements = true;
    this.grikySkillsService.getAchievements(this.company.id).subscribe({
      next: (data) => {
        this.achievementsData = data;
        this.isLoadingAchievements = false;
      },
      error: () => {
        this.achievementsData = null;
        this.isLoadingAchievements = false;
      }
    });
  }

  /** Logros desbloqueados por el usuario. */
  public get unlockedAchievements(): any[] {
    return this.achievementsData?.achievements?.filter(a => a.isUnlocked) || [];
  }

  /** Cantidad de logros desbloqueados (usada también en la fila de estadísticas). */
  public get achievementsUnlockedCount(): number {
    return this.unlockedAchievements.length;
  }

  /** Nombre del próximo logro por desbloquear (para la tarjeta de estadísticas). */
  public get nextAchievementName(): string | null {
    const next = this.achievementsData?.achievements?.find(a => !a.isUnlocked);
    return next?.name || null;
  }

  /** XP restante para el siguiente nivel (null si ya está en el nivel máximo). */
  public get xpForNextLevel(): number | null {
    const level = this.achievementsData?.level;
    if (!level || level.nextXp === null || level.nextXp === undefined) { return null; }
    return Math.max(level.nextXp - (this.achievementsData?.xp || 0), 0);
  }

  /** Número del siguiente nivel (nivel actual + 1), para el texto "faltan X XP para nivel Y". */
  public get nextLevelNumber(): number {
    return (this.achievementsData?.level?.level || 0) + 1;
  }

  /** % de progreso hacia el siguiente nivel, según xp/nextXp del nivel actual. */
  public get levelProgressPercent(): number {
    const level = this.achievementsData?.level;
    if (!level || !level.nextXp) { return 0; }
    const xp = this.achievementsData?.xp || 0;
    return Math.min(100, Math.max(0, Math.round((xp / level.nextXp) * 100)));
  }

  /** Horas totales dedicadas, sumando el tiempo real de actividad (eventDuration) de todos los contenidos.
   *  Ojo: contenido externo sin seguimiento de tiempo (Netzun, Learnlight, LinkedIn Learning) puede no sumar aquí. */
  public get totalDedicatedHours(): number {
    const totalSeconds = (this.allContents || []).reduce(
      (sum: number, c: any) => sum + (c?.data?.eventDuration || 0), 0
    );
    return Math.floor(totalSeconds / 3600);
  }

  /** Fecha del certificado más reciente ("mar 2024"), para el grupo RECONOCIMIENTOS sin Skill. */
  public get latestCertificateLabel(): string | null {
    const latest = this.certificates?.[0]?.certificatedDate;
    if (!latest) { return null; }
    const monthLabel = this.months.find(m => m.v === latest.getMonth() + 1)?.l.toLowerCase() || '';
    return `${monthLabel} ${latest.getFullYear()}`;
  }

  /** URL de la imagen del badge de un logro. */
  public achievementBadgeUrl(icon: string): string {
    return `assets/badges/pack-achievements/es_ES/${icon}.png`;
  }

  /**
   * Formatea el timeline del backend (ej. "6_months") a texto legible ("6 meses").
   */
  private formatTimeline(timeline: string | null | undefined): string {
    if (!timeline) {
      return '—';
    }
    const [num, unit] = timeline.split('_');
    const map: { [key: string]: string } = {
      day: 'día', days: 'días',
      week: 'semana', weeks: 'semanas',
      month: 'mes', months: 'meses',
      year: 'año', years: 'años'
    };
    return `${num} ${map[unit] || unit || ''}`.trim();
  }

  public getContent(page: number = 1): void {
    this.contents = [];
    this.isLoading = true;
    this.currentPage = 1;

    // Traer TODOS los cursos (todas las páginas) para que los filtros y la
    // paginación apliquen sobre la totalidad y no solo sobre una página.
    const params: any = {
      company_id: this.company.id,
      per_page: 1000,
      page: 1
    };

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      params['q'] = this.searchQuery.trim();
    }

    this.contentProvider.getContents(params).subscribe((data) => {
      this.contents = data.contents || [];
      this.allContents = data.contents || [];
      this.allContentsShuffled = this.shuffle(this.allContents);
      this.computeStatusCounts();
      this.pagination = data.pagination;
      this.isLoading = false;
      // Filtrar sobre la totalidad y paginar del lado del cliente
      this.applyStatusFilter();
      // Actualizar cursos para aprender
      this.updateContinueLearningCourses();
    });
  }

  public getAcademicProfile(): void {
    this.isLoadingProfile = true;

    this.learnApi.getAcademicProfileResume(this.company.id).subscribe({
      next: (data: AcademicProfileResume) => {
        this.academicProfile = data;
        // Actualizar estadísticas de cursos
        this.updateCourseStats();
        // Derivar los certificados obtenidos de los contenidos registrados
        this.buildCertificates();
        //console.log(this.academicProfile);
      },
      error: (err) => {
        console.error('Error al cargar el resumen académico', err);
      },
      complete: () => {
        this.isLoadingProfile = false;
        this.isLoadingSkeleton = false;
      }
    });
  }

  /**
   * Construye la lista de "Últimos certificados" a partir de `certificatesObtained`, que el API
   * lee de course_progress_histories. No se reconstruye desde los contenidos inscritos: esas
   * listas parten del catálogo vivo y perdían certificados de contenido que ya no está en los
   * campus del alumno. Se ordenan por fecha de certificación desc.
   */
  private buildCertificates(): void {
    const data = this.academicProfile?.data;
    if (!data) {
      this.certificates = [];
      return;
    }

    this.certificates = (data.certificatesObtained?.data || [])
      .filter((item: any) => item?.certificatePath && item.certificatePath.trim() !== '')
      .map((item: any) => {
        const image = this.buildCertificateImage(item.contentImage, item.contentId);
        return {
          contentName: item.contentName || '',
          providerName: item.providerName || '',
          certificatePath: item.certificatePath,
          certificatedAt: item.certificatedAt || '',
          certificatedDate: item.certificatedAt ? new Date(item.certificatedAt.replace(' ', 'T')) : null,
          image,
          // Logo de marca del proveedor (mismo patrón que content-card.getLogo).
          providerLogo: this.buildProviderLogo(item)
        };
      })
      .sort((a: any, b: any) => (b.certificatedAt || '').localeCompare(a.certificatedAt || ''));

    this.certPage = 0;
  }

  /**
   * Resuelve la URL de imagen del certificado (path relativo image/xxx.png o URL absoluta).
   */
  private buildCertificateImage(image: string | null, id: number = 0): string {
    if (!image) {
      const normalizedId = (id || 0) % 7;
      return `https://static.myskillcamp.com/lxp/images/logo-empty-state-${normalizedId}.webp`;
    }
    let url = image.startsWith('http') ? image : `${environment.envVar.API_URL}/stockage/${image}`;
    if (!environment.production) {
      url = url.replace('https://api-upgraded.myskillcamp.com.deltabluecdn.com', 'https://api.myskillcamp.com');
    }
    return url.includes('?') ? url : `${url}?size=512`;
  }

  /**
   * Logo de marca del proveedor para la card del certificado. Replica el patrón de
   * content-card.getLogo: SVG de integraciones alojado por el slug/clave del proveedor
   * (ej. netzun, aws, platzi). Devuelve '' si no hay proveedor, para que la card
   * caiga a la imagen del contenido.
   */
  private buildProviderLogo(item: any): string {
    const slug = (item?.providerSlug || item?.providerName || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
    if (!slug) {
      return this.fallbackProviderLogo;
    }
    return `https://static.myskillcamp.com/images/integrations/${slug}.svg`;
  }

  /** Logo de marca de respaldo (logo de la empresa o de Griky) cuando el proveedor no tiene SVG. */
  private get fallbackProviderLogo(): string {
    return this.companyLogo || 'https://webservices.griky.co/cdn/img/Griky-Logotipo.webp';
  }

  /** Si el logo del proveedor no carga, cae al logo de marca (no a la portada del curso). */
  public onLogoError(event: Event, _item: any): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.fallbackProviderLogo;
    if (img.src !== fallback) {
      img.src = fallback;
    }
  }

  // ===== Carrusel de "Últimos certificados" =====
  public certPage: number = 0;
  public certPerPage: number = 4;

  /** Ítems del carrusel: certificados + la tarjeta final "Sigue aprendiendo". */
  private get certItems(): any[] {
    return [...this.certificates, { learnMore: true }];
  }

  /** Todos los ítems del carrusel (usado por el slider táctil en móvil). */
  public get allCertItems(): any[] {
    return this.certItems;
  }

  /** Número total de páginas del carrusel. */
  public get certTotalPages(): number {
    return Math.max(1, Math.ceil(this.certItems.length / this.certPerPage));
  }

  /** Ítems visibles en la página actual del carrusel. */
  public get pagedCertItems(): any[] {
    const start = this.certPage * this.certPerPage;
    return this.certItems.slice(start, start + this.certPerPage);
  }

  public certPrev(): void {
    if (this.certPage > 0) { this.certPage--; }
  }

  public certNext(): void {
    if (this.certPage < this.certTotalPages - 1) { this.certPage++; }
  }

  public goCertPage(index: number): void {
    if (index >= 0 && index < this.certTotalPages) { this.certPage = index; }
  }

  /** Abre el PDF del certificado en una nueva pestaña. */
  public openCertificateLink(cert: any): void {
    if (cert?.certificatePath) {
      window.open(cert.certificatePath, '_blank');
    }
  }

  // ===== Modal de certificado =====
  public selectedCertificate: any = null;
  public showCertModal: boolean = false;
  public logoError: boolean = false;

  // Confetti del modal "Felicitaciones": 28 partículas con posición/tiempo/forma deterministas.
  private readonly confettiColors = ['#FF2D55', '#22304C', '#FFB020', '#38B2AC', '#FF93A8'];
  public readonly confettiPieces = Array.from({ length: 28 }, (_, i) => {
    const isCircle = i % 4 === 0;
    const width = i % 3 === 0 ? 10 : 7;
    const height = isCircle ? width * 0.7 : (i % 2 === 0 ? width * 0.45 : width);
    return {
      left: (i * 37) % 100,
      delay: (i % 9) * 0.18,
      duration: 2.6 + (i % 5) * 0.35,
      width,
      height,
      radius: isCircle ? 9999 : 2,
      color: this.confettiColors[i % this.confettiColors.length],
    };
  });

  /** Logo de la empresa (mismo origen que el header) con CDN corregido en dev. */
  public get companyLogo(): string {
    let url: string = this.company?.logo || this.company?.experience?.displayPicture || '';
    if (!url) {
      return '';
    }
    if (!environment.production) {
      url = url.replace(
        'https://api-upgraded.myskillcamp.com.deltabluecdn.com',
        'https://api.myskillcamp.com'
      );
    }
    return url.includes('?') ? url : `${url}?size=512`;
  }

  public openCertModal(cert: any): void {
    this.selectedCertificate = cert;
    this.showCertModal = true;
  }

  public closeCertModal(): void {
    this.showCertModal = false;
  }

  /**
   * Actualiza las estadísticas de cursos basadas en el perfil académico
   */
  private updateCourseStats(): void {
    if (this.academicProfile) {
      const totalEnrolled = this.academicProfile.totalSubscribedContents || 0;
      const completed = this.academicProfile.completedContents || 0;
      const inProgress = this.academicProfile.contentsInProgress || 0;
      
      // Si no hay cursos completados, usar datos ficticios para demostración
      if (completed === 0) {
        const demoCompleted = 8;
        const demoTotal = totalEnrolled || 22;

        this.courseStats = {
          totalCourses: demoTotal,
          coursesInProgress: demoTotal - demoCompleted,
          completedCourses: demoCompleted,
          totalEnrolled: demoTotal,
          averageGrade: 88,
          totalProgress: Math.round((demoCompleted / demoTotal) * 100),
          // marca para que la UI muestre que son datos de demostración
          // (NO son valores reales del usuario)
          isDemo: true
        };
      } else {
        // Datos reales
        this.courseStats = {
          totalCourses: totalEnrolled,
          coursesInProgress: inProgress,
          completedCourses: completed,
          totalEnrolled: totalEnrolled,
          averageGrade: undefined,
          totalProgress: Math.round((completed / totalEnrolled) * 100),
          isDemo: false
        };
      }

      // Actualizar cursos para la tarjeta "Continúa aprendiendo"
      this.updateContinueLearningCourses();
    }
  }

  /**
   * Actualiza los cursos para la tarjeta "Continúa donde lo dejaste".
   * Excluye los cursos finalizados (100%) y el que ya se muestra en el banner;
   * de los restantes muestra los 3 siguientes.
   */
  private updateContinueLearningCourses(): void {
    // Los contenidos vienen de getContents (course-subscriptions), misma estructura
    // que "Mis cursos" (list.component): los campos reales están bajo course.data.*
    const source = (this.recentViews && this.recentViews.length > 0)
      ? this.recentViews
      : (this.contents || []);

    const bannerId = this.lastViewedCourse ? this.lastViewedCourse.id : null;
    const candidates = source.filter((c: any) => {
      const progress = c?.data?.progress ?? 0;
      const id = c?.data?.id ?? c?.id;
      // Sin finalizados, sin el del banner y, sin Skill habilitado, sin rutas de aprendizaje
      return progress < 100 && id !== bannerId && (this.grikySkillsEnabled || c.type !== 'traject');
    });

    this.continueLearningCourses = candidates.slice(0, 3).map((course: any) => ({
      id: course.data?.id ?? course.id,
      type: course.type,
      title: course.data?.title || course.title || 'Sin título',
      image: this.getContentPicture(course),
      progress: course.data?.progress ?? 0,
      provider: course.data?.provider ?? course.provider,
      providerName: this.getProviderName(course),
      providerSlug: course.providerSlug,
      urlDetail: course.data?.urlDetail || course.urlDetail || '',
      lessons: course.data?.contentDetail?.length || 20,
      totalLessons: course.data?.contentDetail?.length || 20
    }));
  }

  public getCalendarEventsApi(day: any | null, month: any | null): void {
    this.isLoadingEvents = true;

    if (day && month) {
      this.startDate = `${this.currentDate.getFullYear()}-${month}-${day}`;
    } else {
      this.startDate = `${this.currentDate.getFullYear()}-${this.currentDate.getMonth() + 1}-${this.currentDate.getDate()}`;
    }

    this.learnApi.getCalendarEvents(this.company.id, this.startDate, this.startDate).subscribe({
      next: (data: any) => {
        this.calendarEvents = data.events;
      },
      error: (err) => {
        console.error('Error al cargar el resumen académico', err);
      },
      complete: () => {
        this.isLoadingEvents = false;
        this.isLoadingSkeleton = false;
      }
    });
  }

  public buildRouterLink(urlDetail: string): string[] {
    const cleaned = decodeURIComponent(urlDetail || '');
    const segments = cleaned.split('/');
    return ['/learn', this.company?.slug, ...segments];
  }

  /**
   * Resuelve la URL de imagen de un contenido replicando la lógica de la card
   * de "Mis cursos" (content-card.getCardPictureSrc):
   *  1) data.picture  2) content.picture  3) imagen empty-state de respaldo
   * Aplica además el reemplazo de CDN para entornos no productivos.
   */
  public getContentPicture(content: any): string {
    const data = content?.data || content || {};
    let url: string = data?.picture || content?.picture || '';

    if (url) {
      if (!environment.production) {
        url = url.replace(
          'https://api-upgraded.myskillcamp.com.deltabluecdn.com',
          'https://api.myskillcamp.com'
        );
      }
      const provider = data?.provider;
      const showSizeParam =
        (typeof provider === 'string' && provider !== 'linkedin-learning') ||
        (typeof provider === 'object' && provider?.key !== 'linkedin');
      url += showSizeParam ? '?size=512' : '';
      return url;
    }

    // Respaldo: imagen empty-state determinística (igual que content-card)
    const id = data?.id ?? content?.contentId ?? 0;
    const normalizedId = id % 7;
    return `https://static.myskillcamp.com/lxp/images/logo-empty-state-${normalizedId}.webp`;
  }

  /**
   * Devuelve la clave i18n del tipo de contenido para "Últimos vistos":
   * rutas de aprendizaje (traject) vs cursos.
   */
  public getContentTypeKey(content: any): string {
    return content?.type === 'traject'
      ? 'api.content-type.traject'
      : 'api.content-type.course';
  }

  /**
   * Nombre del proveedor/plataforma de un contenido (objeto o string).
   * Cae al nombre de la empresa si no hay proveedor.
   */
  public getProviderName(content: any): string {
    const provider = content?.data?.provider ?? content?.provider;
    const name = (provider && typeof provider === 'object') ? provider.name : provider;
    return name || this.company?.name || '';
  }

  /**
   * Fallback de imagen cuando una URL falla al cargar (onerror del <img>).
   */
  public onImageError(event: Event, content: any): void {
    const id = (content?.data?.id ?? content?.contentId ?? 0) % 7;
    (event.target as HTMLImageElement).src =
      `https://static.myskillcamp.com/lxp/images/logo-empty-state-${id}.webp`;
  }

  public validateImage(image: string | null): string {
    if (image === null) {
      const id = parseInt(image, 10);
      const normalizedId = id % 7;
      return `https://static.myskillcamp.com/lxp/images/logo-empty-state-${normalizedId}.webp`;
    } else {
      return image;
    }
  }

  /**
   * Cambia el filtro de estado del curso y actualiza la tabla
   */
  public setStatusFilter(status: 'all' | 'in-progress' | 'completed' | 'not-started'): void {
    this.statusFilter = status;
    this.currentPage = 1; // reiniciar a la primera página al cambiar de filtro
    this.applyStatusFilter();
  }

  /**
   * Aplica el filtro de estado sobre la TOTALIDAD de los cursos (todas las páginas)
   * y recalcula la paginación según el subconjunto filtrado.
   * Mismos umbrales que "Mis cursos" (list.component.applyStatusFilter):
   *  - Finalizado: progress >= 100
   *  - En curso:   progress > 0 && < 100
   * En "Todos" los registros se muestran en orden ALEATORIO (estable).
   */
  private applyStatusFilter(): void {
    // Sin Skill habilitado, las rutas de aprendizaje no se muestran en la tabla (son parte de Skill).
    const base = this.grikySkillsEnabled
      ? this.allContents
      : this.allContents.filter(c => c.type !== 'traject');
    const baseShuffled = this.grikySkillsEnabled
      ? this.allContentsShuffled
      : this.allContentsShuffled.filter(c => c.type !== 'traject');

    if (this.statusFilter === 'all') {
      this.filteredAll = baseShuffled;
    } else if (this.statusFilter === 'in-progress') {
      this.filteredAll = base.filter(c => {
        const p = c?.data?.progress ?? 0;
        return p > 0 && p < 100;
      });
    } else if (this.statusFilter === 'completed') {
      this.filteredAll = base.filter(c => (c?.data?.progress ?? 0) >= 100);
    } else if (this.statusFilter === 'not-started') {
      this.filteredAll = base.filter(c => (c?.data?.progress ?? 0) === 0);
    } else {
      this.filteredAll = base;
    }

    // Búsqueda en tiempo real por título (ignora mayúsculas y acentos)
    const query = (this.searchQuery || '').trim();
    if (query) {
      const nq = this.normalizeText(query);
      this.filteredAll = this.filteredAll.filter(c =>
        this.normalizeText(c?.data?.title || '').includes(nq)
      );
    }

    // Recalcular paginación según el conjunto filtrado
    this.totalPages = Math.max(1, Math.ceil(this.filteredAll.length / this.perPage));
    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
    this.paginateFiltered();
  }

  /**
   * Corta el conjunto filtrado según la página actual (paginación client-side).
   */
  private paginateFiltered(): void {
    const start = (this.currentPage - 1) * this.perPage;
    this.filteredContents = this.filteredAll.slice(start, start + this.perPage);
  }

  /**
   * Genera la lista de páginas a mostrar en la paginación de forma compacta:
   * siempre la primera y la última, la página actual con sus vecinas, y '...'
   * en los tramos omitidos. Ej: 1 … 5 6 [7] 8 9 … 21
   */
  public getPageItems(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const items: (number | string)[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) {
      items.push('...');
    }
    for (let i = start; i <= end; i++) {
      items.push(i);
    }
    if (end < total - 1) {
      items.push('...');
    }
    items.push(total);
    return items;
  }

  /**
   * Cambia de página (client-side) sobre el conjunto ya filtrado.
   */
  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.paginateFiltered();
  }

  /**
   * Cuenta los contenidos por estado de progreso (mismos umbrales que "Mis cursos"):
   *  - Finalizados: progress >= 100
   *  - En curso:    progress > 0 && < 100
   *  - Sin empezar: progress === 0
   *
   * IMPORTANTE: aquí NO se separa por tipo. Cuenta TODO junto — tanto cursos
   * como rutas de aprendizaje (type === 'traject'). Por eso "Cursos inscritos /
   * en progreso / finalizados" del perfil académico suma ambos.
   * (En el overview sí se separan por tipo: ver overview.component.loadCompletionStats)
   */
  private computeStatusCounts(): void {
    const list = this.allContents || [];
    let completed = 0, inProgress = 0, notStarted = 0;
    for (const c of list) {
      const p = c?.data?.progress ?? 0;
      if (p >= 100) { completed++; }
      else if (p > 0) { inProgress++; }
      else { notStarted++; }
    }
    this.statusCounts = { completed, inProgress, notStarted, total: list.length };
  }

  /**
   * Devuelve una copia barajada del array (Fisher-Yates), sin mutar el original.
   */
  private shuffle(arr: any[]): any[] {
    const result = [...(arr || [])];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }
}